import fetcher from 'api/fetcher';
import { AxiosError } from 'axios';
import { push } from 'connected-react-router';
import { mappingCampaignStateToDTO } from 'utils/campaign/mappers';
import { CampaignProcess } from 'utils/constants/campaign';
import { CampaignStatus } from 'utils/constants/dashboard';
import { MagritteNotificationName } from 'utils/constants/notification';

import generateTextFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_design/generate_text_form_submit';
import campaignCreateVideoFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/campaign_create_video_form_submit';
import campaignCreateCropSaveFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/common/campaign_create_crop_save_form_submit';
import { urlParser } from '@hh.ru/browser-api-utils';

import { SAVE_CAMPAIGN_URL } from 'components/CpcFlexibleCreate/CpcActions/saveCampaign';

import { setShouldSendUXFBEvent } from 'store/autoReducers';
import { resetAiImageGeneration } from 'store/models/aiImageGeneration';
import { resetBannerTextGeneration } from 'store/models/bannerTextGeneration';
import { selectCampaignProcess } from 'store/models/createCampaign';
import { addMagritteNotification } from 'store/models/notification';
import { AppThunkAction } from 'store/utils';

type SaveCampaignAndBanner = (setSaving: (isSaving: boolean) => void, dashboardRoute: string) => AppThunkAction;

const blockBannedCampaign = (bannedCampaignId: number) => {
    const queryStr = urlParser.stringify({
        newStatus: CampaignStatus.Blocked,
        changeSource: 'CLICKME_COPY_CAMPAIGN',
    });

    return fetcher.put(`/api/v1/campaigns/${bannedCampaignId}/status?${queryStr}`);
};

export const saveCampaignAndBanner: SaveCampaignAndBanner = (setSaving, dashboardRoute) => {
    return (dispatch, getState) => {
        const { createBanner, createCampaign, profRoles } = getState();
        const campaignProcess = selectCampaignProcess(getState());
        const { bannerTextWasGenerated, bannerTextGenerationCount } = getState().bannerTextGeneration;

        const body = {
            ...mappingCampaignStateToDTO({
                ...createCampaign,
                ...createBanner,
                profRoles,
            }),
        };

        return fetcher
            .post<unknown, { campaignId: number }>(SAVE_CAMPAIGN_URL, body)
            .then(({ campaignId }): void => {
                campaignCreateCropSaveFormSubmit({ campaignId });

                if (createBanner.bannerVideo) {
                    campaignCreateVideoFormSubmit({
                        vacancyId: createBanner.bannerVacancyId ? String(createBanner.bannerVacancyId) : null,
                        campaignId: String(campaignId),
                    });
                }

                if (bannerTextWasGenerated) {
                    generateTextFormSubmit({
                        vacancyId: String(createBanner.bannerVacancyId),
                        campaignId: String(campaignId),
                        triesCount: String(bannerTextGenerationCount),
                    });
                }

                if (campaignProcess === CampaignProcess.Create || campaignProcess === CampaignProcess.Copy) {
                    dispatch(setShouldSendUXFBEvent(true));
                }
                dispatch(resetAiImageGeneration(null));
                dispatch(resetBannerTextGeneration());

                if (campaignProcess === CampaignProcess.Copy && createCampaign.bannedCampaignId) {
                    void blockBannedCampaign(createCampaign.bannedCampaignId).then(() => {
                        dispatch(push(dashboardRoute));
                    });
                    return;
                }

                dispatch(push(dashboardRoute));
            })
            .then(() => {
                if (campaignProcess === CampaignProcess.Copy) {
                    dispatch(addMagritteNotification(MagritteNotificationName.CampaignCopySuccess));
                }
            })
            .catch((errors: AxiosError<Record<string, string>>) => {
                if (errors?.response?.status === 400) {
                    campaignCreateCropSaveFormSubmit({ errors: JSON.stringify(errors.response.data), campaignId: 0 });
                }
                setSaving(false);
                throw errors;
            });
    };
};
